/**
 * Persisted User Profile Sanitizer (ECHO-2318 / VAPT-006)
 *
 * The user store is persisted to localStorage, which survives the browser session and is
 * readable by any script on the origin. Only non-identifying fields are written there; the
 * personal data (login username, name, email, NRIC, phone, employee details, locations, audit
 * trail) stays in memory and is reloaded on every bootstrap:
 * - `saveUserSession` restores id/username/name/email from the tab-scoped session user, and
 * - `initializeHealthcareContext` refetches the full staff profile because `fullName` is absent.
 */

import type { UserProfile } from "../../types";

/** Optional profile fields that are safe to keep across reloads. */
const PERSISTED_OPTIONAL_KEYS = [
  "userId",
  "avatar",
  "staffId",
  "specialtyId",
  "status",
  "roles",
  "permissions",
] as const satisfies readonly (keyof UserProfile)[];

type PersistedOptionalKey = (typeof PERSISTED_OPTIONAL_KEYS)[number];

/**
 * Profile shape written to localStorage. `username`, `name` and `email` are required by
 * `UserProfile`, so they are kept as empty strings; this keeps the sanitized value assignable to
 * `UserProfile` without a cast.
 *
 * `id` holds the login identifier (often an e-mail address) until the staff profile has loaded,
 * so it is never persisted verbatim: it is replaced by the staff UUID in `userId`, or `""` when
 * that is not known yet.
 */
export type PersistedUserProfile = Pick<UserProfile, "id" | PersistedOptionalKey> & {
  username: "";
  name: "";
  email: "";
};

function copyIfDefined<K extends PersistedOptionalKey>(
  target: Pick<UserProfile, PersistedOptionalKey>,
  source: UserProfile,
  key: K,
): void {
  const value = source[key];
  if (value !== undefined) {
    target[key] = value;
  }
}

/**
 * Reduce a user profile to the allowlisted, non-identifying fields before it is persisted.
 * Any field not listed here (including fields added to `UserProfile` later) is dropped.
 */
export function toPersistedUserProfile(profile: UserProfile | null): PersistedUserProfile | null {
  if (!profile) return null;

  const persisted: PersistedUserProfile = {
    id: profile.userId ?? "",
    username: "",
    name: "",
    email: "",
  };

  for (const key of PERSISTED_OPTIONAL_KEYS) {
    copyIfDefined(persisted, profile, key);
  }

  return persisted;
}
